/**
 * RecallPanel — the canonical Recall UI used both inside the meeting room
 * (live) and on the post-meeting details page.
 *
 * Pulls everything via /api/recall/meeting/{id} and renders sections in this
 * canonical order:
 *   1. Participants
 *   2. Notes
 *   3. AI Summary
 *   4. Recording
 *   5. Tasks / Action Items
 *   6. Follow Up
 *   7. Follow-Up Email Draft
 *   8. Transcript          ← always last
 *
 * Toggles for AI Summary / Transcription / Recording have been replaced by
 * read-only status indicators (Enabled/Disabled + Captured/Not Captured).
 * Feature controls live on the Meeting Settings tab — never here.
 */
import { useEffect, useState } from "react";
import {
  Download, Sparkles, FileText, Users as UsersIcon, CheckSquare, Mic, Brain,
  Loader2, AlertCircle, Calendar, Activity, ChevronRight, Video, Mail,
  MessageCircle, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ChatText, { formatBytes } from "@/lib/chatHelpers";
import {
  formatLongDateTimeInTz, formatLongDateInTz, userBrowserTimezone,
} from "@/lib/time";
import { buildMeetingFilename, saveBlobAs } from "@/lib/filename";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const SECTIONS = [
  { key: "meeting_details", label: "Meeting details", defaultOn: true },
  { key: "description", label: "Description", defaultOn: true },
  { key: "participants", label: "Participants", defaultOn: true },
  { key: "ai_summary", label: "Summary", defaultOn: true },
  { key: "action_items", label: "Action items", defaultOn: true },
  { key: "decisions", label: "Decisions", defaultOn: true },
  { key: "follow_up", label: "Follow Up notes", defaultOn: true },
  { key: "tasks", label: "Tasks", defaultOn: false },
  { key: "recording_references", label: "Recording references", defaultOn: false },
  { key: "transcript", label: "Transcript", defaultOn: false },
  { key: "notes", label: "Notes", defaultOn: false },
  { key: "activity_timeline", label: "Activity timeline", defaultOn: false },
  { key: "follow_up_email_draft", label: "Follow-Up Email Draft", defaultOn: false },
];

export default function RecallPanel({ meetingId, compact = false, onChanged, reloadKey = 0, onNavigateToFollowup }) {
  const { user } = useAuth();
  const tz = user?.timezone || userBrowserTimezone();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/recall/meeting/${meetingId}`);
      setRecord(r.data);
      onChanged?.();
    } catch {
      toast.error("Could not load Recall");
    } finally { setLoading(false); }
  };
  // Re-fetch whenever the parent bumps `reloadKey` (e.g., after the AI
  // Summary tab saves an edit, the Follow-up tab saves items, etc.) so the
  // Recall view is never stale relative to the rest of the page.
  useEffect(() => { if (meetingId) load(); /* eslint-disable-next-line */ }, [meetingId, reloadKey]);

  if (loading) return <div className="text-sm text-[#82828c] p-4">Loading Recall…</div>;
  if (!record?.meeting) return <div className="text-sm text-[#82828c] p-4">No meeting record.</div>;

  const m = record.meeting;
  const summary = record.summary || {};
  const transcript = record.transcript || {};
  const followup = record.followup || {};
  const notes = record.notes || [];
  const tasks = record.tasks || [];
  const participants = record.participants || [];
  const recordings = record.recordings || [];
  const chat = record.chat || [];

  // ── Truthful "Captured?" derivations ────────────────────────────────────
  const aiEnabled = m.ai_summary_enabled !== false;
  const aiCaptured = !!(summary?.summary_text || "").trim();
  // Transcription is considered "Enabled" if EITHER the meeting was
  // pre-configured with transcription on OR a runtime transcript record
  // exists (i.e. the host actually started Agora STT during the meeting,
  // even if the form switch was off). Otherwise reading purely from the
  // form setting falsely shows "Disabled" for ad-hoc transcribed calls.
  const tStatus = (transcript?.status || "").toLowerCase();
  const transcriptionAttempted = tStatus && tStatus !== "disabled";
  const transcriptionEnabled = !!m.transcription_enabled || transcriptionAttempted;
  const transcriptCaptured = !!(transcript?.full_text || "").trim();
  const recordingEnabled = !!m.recording_enabled;
  const recordingCaptured = recordings.length > 0;
  // We no longer accept browser-only ("webspeech") transcripts as a
  // meeting transcript source (the /transcript/append endpoint now
  // returns 410 Gone). Any historical webspeech rows are filtered out
  // at the backend, but we keep this flag here to flag stale data in
  // the UI if it ever surfaces.
  const transcriptOnlyLocalMic =
    transcriptCaptured && (transcript?.provider || "").toLowerCase().startsWith("webspeech");
  // Surface a useful "Failed" or "Live" sub-state for the badge
  let transcriptStateLabel = null;
  if (tStatus === "failed") transcriptStateLabel = "Failed";
  else if (tStatus === "live" || tStatus === "starting") transcriptStateLabel = "Still running";
  else if (tStatus === "completed" && !transcriptCaptured) transcriptStateLabel = "Ended with no captured audio";

  const downloadSection = async (section, format) => {
    try {
      const res = await api.get(`/recall/meeting/${meetingId}/download`, {
        params: { section, format },
        responseType: "blob",
      });
      const ext = format === "csv" ? "csv" : "txt";
      const filename = buildMeetingFilename({
        title: m.title || "Meeting",
        whenIso: m.actual_start || m.start_time,
        downloadType: section.replace("_", "-"),
        extension: ext,
        timezone: tz,
      });
      saveBlobAs(res.data, filename);
    } catch {
      toast.error("Could not download");
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="recall-panel">
      {/* Header: title + export */}
      <div className="bloq-card p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="bloq-eyebrow">Recall</p>
            <h2 className="bloq-headline text-2xl mt-1">{m.title}</h2>
            <p className="text-xs text-[#82828c] mt-1">
              {formatLongDateInTz(m.start_time, tz)} · {(m.status || "").toUpperCase()}
            </p>
          </div>
          <button
            onClick={() => setShowExport(true)}
            className="bloq-btn-primary inline-flex items-center gap-2"
            data-testid="recall-export-btn"
          >
            <Download className="w-4 h-4" /> Export Recall Report
          </button>
        </div>

        {/* Status indicators — read-only. */}
        <div className="grid sm:grid-cols-3 gap-3 mt-5 pt-5 border-t border-[#1a1a1f]/06">
          <StatusCard
            icon={Brain} label="AI summary"
            enabled={aiEnabled} captured={aiCaptured}
            testid="recall-status-ai-summary"
          />
          <StatusCard
            icon={Mic} label="Transcription"
            enabled={transcriptionEnabled || transcriptCaptured}
            captured={transcriptCaptured}
            hint={transcriptStateLabel || (transcriptOnlyLocalMic ? "Local microphone only" : undefined)}
            testid="recall-status-transcription"
          />
          <StatusCard
            icon={Video} label="Recording"
            enabled={recordingEnabled || recordingCaptured}
            captured={recordingCaptured}
            testid="recall-status-recording"
          />
        </div>
      </div>

      {compact ? null : (
        <>
          {/* 1. Participants */}
          <Section title={`Participants (${participants.length})`} icon={UsersIcon} actions={
            participants.length > 0 ? <DownloadBtn onClick={() => downloadSection("participants", "csv")} /> : null
          }>
            {participants.length > 0 ? (
              <ul className="flex flex-col gap-1.5">
                {participants.map((p) => (
                  <li key={p.participant_id} className="text-sm flex items-center justify-between gap-2">
                    <span>{p.name} <span className="text-[#82828c] text-xs">· {p.role}</span></span>
                    <span className="text-[11px] text-[#82828c]">
                      Joined {formatLongDateTimeInTz(p.joined_at, tz)}
                      {p.left_at && <> · Left {formatLongDateTimeInTz(p.left_at, tz)}</>}
                    </span>
                  </li>
                ))}
              </ul>
            ) : <EmptyState label="No participants tracked." />}
          </Section>

          {/* 3. AI Summary — preview only; full editing happens in Follow-up. */}
          <Section
            title="AI summary" icon={Sparkles}
            editLink={onNavigateToFollowup
              ? { onClick: () => onNavigateToFollowup("followup-summary"), testid: "recall-edit-summary" }
              : { to: `#followup-summary`, testid: "recall-edit-summary" }}
            actions={summary.summary_text ? <DownloadBtn onClick={() => downloadSection("summary", "txt")} /> : null}
          >
            {summary.summary_text ? (
              <p className="text-sm whitespace-pre-line line-clamp-[12]">{summary.summary_text}</p>
            ) : !aiEnabled ? (
              <EmptyState label="AI summaries are off for this meeting. Enable it in Meeting Settings." />
            ) : (
              <EmptyState label="No summary yet — generate one in the Follow-up tab." />
            )}
          </Section>

          {/* 4. Action items — preview-only; edit in Follow-up. */}
          {(() => {
            const fuItems = (followup.action_items || []).map((it) =>
              typeof it === "string" ? { item_id: `s_${it.slice(0, 12)}`, title: it, status: "todo" } : it
            );
            return (
              <Section
                title={`Action items (${fuItems.length})`} icon={Activity}
                editLink={onNavigateToFollowup
                  ? { onClick: () => onNavigateToFollowup("followup-actions"), testid: "recall-edit-actions" }
                  : { to: `#followup-actions`, testid: "recall-edit-actions" }}
              >
                {fuItems.length === 0 ? (
                  <EmptyState label="No action items yet. Add them in the Follow-up tab." />
                ) : (
                  <ul className="flex flex-col gap-1.5" data-testid="recall-action-items-list">
                    {fuItems.slice(0, 10).map((it) => (
                      <li key={it.item_id} className="flex items-start gap-2 text-sm">
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${it.status === "done" ? "bg-[#82828c]" : "bg-[#005e7a]"}`} />
                        <span className={it.status === "done" ? "line-through text-[#82828c]" : ""}>{it.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            );
          })()}

          {/* 5. Decisions — preview-only; edit in Follow-up. */}
          <Section
            title={`Decisions (${(record.decisions || []).length})`} icon={CheckSquare}
            editLink={onNavigateToFollowup
              ? { onClick: () => onNavigateToFollowup("followup-decisions"), testid: "recall-edit-decisions" }
              : { to: `#followup-decisions`, testid: "recall-edit-decisions" }}
          >
            {(record.decisions || []).length === 0 ? (
              <EmptyState label="No decisions captured yet — edit in the Follow-up tab." />
            ) : (
              <ul className="flex flex-col gap-1.5" data-testid="recall-decisions-list">
                {record.decisions.map((d) => (
                  <li key={d.decision_id} className="flex items-center gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#005e7a] flex-shrink-0" />
                    <span>{d.decision_text}</span>
                    {d.source === "ai_generated" && (
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#005e7a]/10 text-[#005e7a]">AI</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Private notes — live notes captured in-room (author-scoped). */}
          <Section title={`Private notes (${notes.length})`} icon={FileText} actions={
            notes.length > 0 ? <DownloadBtn onClick={() => downloadSection("notes", "txt")} /> : null
          }>
            {notes.length > 0 ? (
              <div className="flex flex-col gap-2" data-testid="recall-notes-list">
                {notes.map((n) => (
                  <div key={n.note_id} className="bg-[#f5efe6] rounded-lg p-3">
                    <p className="text-sm whitespace-pre-line">{n.content}</p>
                    <p className="text-[10px] text-[#82828c] mt-1">{formatLongDateTimeInTz(n.created_at, tz)}</p>
                  </div>
                ))}
                <p className="text-[11px] text-[#82828c] mt-1">
                  Notes also appear on the standalone <Link to="/app/notes" className="text-[#005e7a] hover:underline">Notes</Link> page.
                </p>
              </div>
            ) : <EmptyState label="No notes captured." />}
          </Section>

          {/* 6. Notes from the meeting (follow-up notes) — preview only. */}
          <Section
            title="Notes from the meeting" icon={FileText}
            editLink={onNavigateToFollowup
              ? { onClick: () => onNavigateToFollowup("followup-notes"), testid: "recall-edit-notes" }
              : { to: `#followup-notes`, testid: "recall-edit-notes" }}
          >
            {(followup.notes || "").trim() ? (
              <p className="text-sm whitespace-pre-line">{followup.notes}</p>
            ) : (
              <EmptyState label="No follow-up notes yet. Add them in the Follow-up tab." />
            )}
          </Section>

          {/* 7. Recording — non-editable */}
          <Section title={`Recording (${recordings.length})`} icon={Video}>
            {recordings.length > 0 ? (
              <ul className="flex flex-col gap-1.5">
                {recordings.map((r) => (
                  <li key={r.recording_id} className="text-sm flex items-center justify-between gap-2">
                    <span className="truncate">{r.filename || "recording"}</span>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/meetings/recordings/${r.recording_id}/download`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-xs text-[#005e7a] font-medium hover:underline"
                    >
                      Download
                    </a>
                  </li>
                ))}
              </ul>
            ) : <EmptyState
              label={recordingEnabled
                ? "Recording was enabled but no file captured."
                : "Recording was not active for this meeting."} />}
          </Section>

          {/* 8. Tasks — preview only; full editing happens in Tasks tab. */}
          <Section
            title={`Tasks (${tasks.length})`} icon={CheckSquare}
            editLink={{ to: `/app/tasks`, testid: "recall-edit-tasks" }}
            actions={tasks.length > 0 ? <DownloadBtn onClick={() => downloadSection("tasks", "csv")} /> : null}
          >
            {tasks.length > 0 ? (
              <div className="flex flex-col gap-2">
                {tasks.map((t) => (
                  <div key={t.task_id} className="flex items-center justify-between gap-3 p-2.5 rounded-md hover:bg-[#f5efe6]/60">
                    <div className="min-w-0">
                      <p className={`text-sm truncate ${t.status === "done" ? "line-through text-[#82828c]" : ""}`}>{t.title}</p>
                      <p className="text-[10px] text-[#82828c]">{t.status} · {t.priority}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#82828c] flex-shrink-0" />
                  </div>
                ))}
              </div>
            ) : <EmptyState label="No tasks linked to this meeting." />}
          </Section>

          {/* 9. Recap Email Draft — preview only; edit in Follow-up. */}
          <Section
            title="Recap email draft" icon={Mail}
            editLink={onNavigateToFollowup
              ? { onClick: () => onNavigateToFollowup("followup-email"), testid: "recall-edit-email" }
              : { to: `#followup-email`, testid: "recall-edit-email" }}
          >
            {(followup.email_draft || summary.follow_up_email_draft) ? (
              <p className="text-sm whitespace-pre-line line-clamp-[10]">
                {followup.email_draft || summary.follow_up_email_draft}
              </p>
            ) : (
              <EmptyState label="No recap email draft yet. Generate one in the Follow-up tab." />
            )}
          </Section>

          {/* 10. In-meeting chat — non-editable, NOT in the downloadable
              Recall Report (no chat section key exists in ALL_SECTIONS). */}
          <Section title={`In-meeting chat (${chat.length})`} icon={MessageCircle}>
            {chat.length > 0 ? (
              <div className="flex flex-col gap-2" data-testid="recall-chat-list">
                {chat.map((msg) => (
                  <div
                    key={msg.message_id}
                    className="bg-[#f5efe6] rounded-lg p-3"
                    data-testid={`recall-chat-msg-${msg.message_id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className="text-xs font-medium text-[#1a1a1f]">{msg.author_name || "Guest"}</p>
                      <p className="text-[10px] text-[#82828c]">
                        {formatLongDateTimeInTz(msg.created_at, tz)}
                      </p>
                    </div>
                    <ChatText text={msg.text} className="text-sm text-[#4a4a52]" />
                    {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                      <div className="mt-2 flex flex-col gap-1.5" data-testid={`recall-chat-attachments-${msg.message_id}`}>
                        {msg.attachments.map((att) => (
                          <a
                            key={att.attachment_id}
                            href="#"
                            onClick={async (e) => {
                              e.preventDefault();
                              try {
                                const r = await api.get(
                                  `/meetings/${msg.meeting_id}/chat/attachments/${att.attachment_id}/download`,
                                  { responseType: "blob" }
                                );
                                const url = URL.createObjectURL(new Blob([r.data], { type: att.content_type }));
                                const a = document.createElement("a");
                                a.href = url;
                                a.download = att.file_name;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                URL.revokeObjectURL(url);
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || "Download failed");
                              }
                            }}
                            className="inline-flex items-center gap-2 px-2 py-1 bg-white/60 border border-[#1a1a1f]/10 text-xs text-[#005e7a] hover:bg-white"
                            data-testid={`recall-chat-attachment-${att.attachment_id}`}
                          >
                            <FileText className="w-3.5 h-3.5" />
                            <span>{att.file_name}</span>
                            <span className="text-[10px] text-[#82828c]">{formatBytes(att.file_size)}</span>
                          </a>
                        ))}
                      </div>
                    )}
                    {Array.isArray(msg.reactions) && msg.reactions.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1" data-testid={`recall-chat-reactions-${msg.message_id}`}>
                        {msg.reactions.map((r) => (
                          <span
                            key={r.emoji}
                            className="px-1.5 py-0.5 text-[11px] bg-white/60 border border-[#1a1a1f]/10 inline-flex items-center gap-1"
                            title={(r.users || []).map((u) => u.display_name).join(", ")}
                          >
                            <span>{r.emoji}</span>
                            <span className="text-[#4a4a52]">{r.count}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <p className="text-[11px] text-[#82828c] mt-1">
                  Chat is shown here for reference and is <strong>not</strong> included in the downloadable Recall Report.
                </p>
              </div>
            ) : (
              <EmptyState icon={MessageCircle} label="No chat messages were sent during this meeting." />
            )}
          </Section>

          {/* 11. Transcript — non-editable, always last. */}
          <Section title="Transcript" icon={Mic} actions={
            transcript.full_text ? <DownloadBtn onClick={() => downloadSection("transcript", "txt")} /> : null
          }>
            {transcript.full_text ? (
              <>
                <p className="text-[10px] text-[#82828c] uppercase tracking-widest mb-2">
                  {transcript.provider || "unknown"} · {transcript.language || "en"} · {transcript.status}
                </p>
                {transcriptOnlyLocalMic && (
                  <p className="text-[11px] text-amber-700 bg-amber-100/70 rounded p-2 mb-2 leading-snug">
                    Browser transcription captures the local microphone only. Other
                    participants may not be transcribed unless a server-side transcription
                    provider is configured.
                  </p>
                )}
                <pre className="text-sm whitespace-pre-wrap font-sans">{transcript.full_text}</pre>
              </>
            ) : transcriptionEnabled ? (
              <EmptyState label="No transcript captured yet. Live captions in the meeting room save here automatically." />
            ) : (
              <EmptyState icon={AlertCircle} label="Transcription is disabled. Enable it in Meeting Settings." />
            )}
          </Section>
        </>
      )}

      <RecallReportExportDialog
        open={showExport} onClose={() => setShowExport(false)}
        meetingId={meetingId} meetingTitle={m.title} whenIso={m.actual_start || m.start_time}
        tz={tz}
      />
    </div>
  );
}

function Section({ title, icon: Icon, children, actions, editLink }) {
  const handleEdit = (e) => {
    if (editLink?.onClick) {
      e.preventDefault();
      editLink.onClick();
    }
  };
  return (
    <div className="bloq-card p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-[#005e7a]" />}
          <h3 className="bloq-headline text-base">{title}</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {editLink && (
            editLink.onClick ? (
              <button
                onClick={handleEdit}
                className="text-xs text-[#005e7a] font-medium hover:underline inline-flex items-center gap-1"
                data-testid={editLink.testid || "recall-edit-link"}
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            ) : (
              <Link
                to={editLink.to}
                className="text-xs text-[#005e7a] font-medium hover:underline inline-flex items-center gap-1"
                data-testid={editLink.testid || "recall-edit-link"}
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </Link>
            )
          )}
          {actions}
        </div>
      </div>
      {children}
    </div>
  );
}

function EmptyState({ label, icon: Icon = Calendar }) {
  return (
    <div className="flex items-center gap-2 text-sm text-[#82828c] py-2">
      <Icon className="w-4 h-4" /> {label}
    </div>
  );
}

function DownloadBtn({ onClick }) {
  return (
    <button onClick={onClick} className="text-xs text-[#005e7a] font-medium hover:underline inline-flex items-center gap-1">
      <Download className="w-3.5 h-3.5" /> Download
    </button>
  );
}

function StatusCard({ icon: Icon, label, enabled, captured, hint, testid }) {
  return (
    <div className="rounded-lg border border-[#1a1a1f]/08 p-3 flex items-start gap-3" data-testid={testid}>
      <Icon className="w-4 h-4 text-[#005e7a] mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
          <span
            className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${
              enabled
                ? "bg-[#005e7a]/10 text-[#005e7a]"
                : "bg-[#82828c]/15 text-[#82828c]"
            }`}
            data-testid={`${testid}-enabled`}
          >
            {enabled ? "Enabled" : "Disabled"}
          </span>
          <span
            className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${
              captured
                ? "bg-[#16a34a]/15 text-[#16a34a]"
                : "bg-[#82828c]/15 text-[#82828c]"
            }`}
            data-testid={`${testid}-captured`}
          >
            {captured ? "Captured" : "Not Captured"}
          </span>
        </div>
        {hint && <p className="text-[10px] text-[#82828c] mt-1">{hint}</p>}
      </div>
    </div>
  );
}

// Read-only structured action item list displayed inside the Recall Follow Up
// section. The Follow-up tab on the meeting page is the place to edit these
// items — Recall stays a display surface.
function FollowUpActionItems({ followup, summary }) {
  // followup.action_items is the source of truth (already structured because
  // /api/recall/meeting/{id} normalizes it). Fall back to summary.action_items
  // (plain strings) only if the followup doc is empty.
  const items = (followup?.action_items?.length ? followup.action_items
    : (summary?.action_items || []).map((t, i) => ({ item_id: `s_${i}`, title: t, status: "todo", source: "ai_generated" }))
  ) || [];
  if (items.length === 0) {
    return <p className="text-sm text-[#82828c]">No action items yet — generate the AI summary or add one in the Follow-up tab.</p>;
  }
  return (
    <div>
      <p className="text-xs uppercase tracking-widest text-[#82828c] mb-1">Action items</p>
      <ul className="flex flex-col gap-1" data-testid="recall-action-items">
        {items.map((it) => (
          <li key={it.item_id} className="flex items-center gap-2 text-sm">
            <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${it.status === "done" ? "bg-[#16a34a]" : "bg-[#005e7a]"}`} />
            <span className={it.status === "done" ? "line-through text-[#82828c]" : ""}>{it.title}</span>
            {it.source === "ai_generated" && (
              <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded bg-[#005e7a]/10 text-[#005e7a]">AI</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function RecallReportExportDialog({ open, onClose, meetingId, meetingTitle, whenIso, tz }) {
  // Default selection mirrors backend DEFAULT_SECTIONS — Notes are OFF.
  const [selected, setSelected] = useState(SECTIONS.filter((s) => s.defaultOn).map((s) => s.key));
  const [format, setFormat] = useState("pdf");
  const [generating, setGenerating] = useState(false);

  const toggle = (k) => setSelected((arr) => arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(
        `/recall/meeting/${meetingId}/report`,
        { format, sections: selected },
        { responseType: "blob" },
      );
      const ext = format === "pdf" ? "pdf" : "docx";
      const filename = buildMeetingFilename({
        title: meetingTitle, whenIso, downloadType: "recall-report", extension: ext, timezone: tz,
      });
      saveBlobAs(res.data, filename);
      toast.success("Recall Report downloaded");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate report");
    } finally { setGenerating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="recall-export-dialog">
        <DialogHeader><DialogTitle>Export Recall Report</DialogTitle></DialogHeader>
        <p className="text-xs text-[#82828c] -mt-2 mb-3">Choose which sections to include. Empty sections will show a tasteful empty state.</p>

        <div className="flex flex-col gap-2 mb-4 max-h-72 overflow-y-auto pr-1">
          {SECTIONS.map((s) => {
            const on = selected.includes(s.key);
            return (
              <label key={s.key} className="flex items-center gap-2 cursor-pointer py-1.5 px-2 rounded hover:bg-[#f5efe6]">
                <input type="checkbox" checked={on} onChange={() => toggle(s.key)} data-testid={`recall-section-${s.key}`} />
                <span className="text-sm">{s.label}</span>
              </label>
            );
          })}
        </div>

        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs uppercase tracking-widest text-[#82828c]">Format</span>
          <label className="flex items-center gap-1 text-sm">
            <input type="radio" checked={format === "pdf"} onChange={() => setFormat("pdf")} data-testid="recall-format-pdf" /> PDF
          </label>
          <label className="flex items-center gap-1 text-sm">
            <input type="radio" checked={format === "docx"} onChange={() => setFormat("docx")} data-testid="recall-format-docx" /> DOCX
          </label>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="bloq-btn-secondary" disabled={generating}>Cancel</button>
          <button onClick={generate} disabled={generating || selected.length === 0} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="recall-generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download {format.toUpperCase()}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
